// Il riassunto di un risultato in una riga, per la riga chiusa del Diario.
// Il dettaglio completo (previsto vs eseguito) resta in ExerciseDetailAccordion.
import {
	getDisplayEmomRounds,
	hasContent,
	resolveConditioningResultPresentation,
} from "../../../lib/conditioningResultUtils";

const isMeaningful = (value) => {
	if (!hasContent(value)) return false;
	const s = String(value).trim();
	return s !== "0" && s !== "0.0";
};

const formatClock = (totalSeconds) => {
	const sec = Number(totalSeconds);
	if (!Number.isFinite(sec) || sec <= 0) return null;
	const m = Math.floor(sec / 60);
	const s = Math.round(sec % 60);
	return `${m}:${String(s).padStart(2, "0")}`;
};

const formatNumber = (value, digits) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return Number.isInteger(n) ? String(n) : n.toFixed(digits);
};

const repsOf = (row) =>
	hasContent(row?.reps_text)
		? String(row.reps_text)
		: isMeaningful(row?.reps)
			? String(row.reps)
			: null;

// 100 kg × 5 ripetuto tre volte diventa «3 × 100 kg × 5»: le serie uguali di
// fila sono la norma, e scritte una per una la riga non ci sta.
function summarizeSetRows(rows) {
	const labels = rows
		.map((row) => {
			const weight = isMeaningful(row?.weight_kg) ? `${row.weight_kg} kg` : "";
			const reps = repsOf(row);
			if (weight && reps) return `${weight} × ${reps}`;
			return weight || reps || "";
		})
		.filter(Boolean);
	const runs = [];
	labels.forEach((label) => {
		const last = runs[runs.length - 1];
		if (last && last.label === label) last.count += 1;
		else runs.push({ label, count: 1 });
	});
	return runs
		.map(({ label, count }) => (count > 1 ? `${count} × ${label}` : label))
		.join(" · ");
}

function summarizeShooting(shooting) {
	const parts = [];
	const hitFactor = shooting.combined_hit_factor ?? shooting.hit_factor;
	if (hitFactor != null) parts.push(`HF ${formatNumber(hitFactor, 2)}`);
	else if (shooting.total_points != null)
		parts.push(`${formatNumber(shooting.total_points, 1)} pt`);
	const time = shooting.best_time_seconds ?? shooting.total_time_seconds;
	if (time != null && Number.isFinite(Number(time)))
		parts.push(`${Number(time).toFixed(2)}s`);
	if (shooting.accuracy_pct != null)
		parts.push(`${Math.round(Number(shooting.accuracy_pct))}%`);
	return parts.join(" · ");
}

function summarizeSchema(result, exercise) {
	const values = result?.schema_values;
	if (!values || typeof values !== "object") return "";
	const schema =
		exercise?.result_schema ||
		result?.conditioning_config?.result_schema ||
		[];
	return schema
		.map((field) => {
			const raw = values[field?.field_id || field?.name];
			if (!hasContent(raw)) return null;
			const unit = String(field?.unit || "").toLowerCase();
			if (["seconds", "minutes", "minutes_seconds", "time"].includes(unit))
				return formatClock(raw);
			if (unit === "kg") return `${raw} kg`;
			if (unit === "meters") return `${raw} m`;
			return String(raw);
		})
		.filter(Boolean)
		.join(" · ");
}

const joinWords = (...parts) =>
	parts
		.map((part) => (part == null ? "" : String(part).trim()))
		.filter(Boolean)
		.join(" ");

// «10 Deadlift», o il testo dell'elemento scritto a mano. I minuti di un EMOM
// ripetono gli stessi movimenti: ognuno compare una volta.
function movementList(items) {
	const seen = new Set();
	return items
		.map((item) =>
			item?.is_custom
				? String(item?.custom_text || "").trim()
				: joinWords(item?.reps, item?.exercise_name),
		)
		.filter((label) => label && !seen.has(label) && seen.add(label));
}

const emomRoundItems = (exercise) =>
	getDisplayEmomRounds(exercise).flatMap((round) => round.items);

// Il testo libero si legge riga per riga, e la prima da sola («team of 2:»,
// «All for time:») non dice niente: le righe si mettono in fila, e quella
// che finisce coi due punti fa da intestazione alla successiva.
function freeTextLine(text) {
	return String(text || "")
		.split("\n")
		.map((line) => line.trim())
		.filter(Boolean)
		.reduce(
			(acc, line) =>
				!acc ? line : acc.endsWith(":") ? `${acc} ${line}` : `${acc} · ${line}`,
			"",
		);
}

/**
 * Cosa c'era da fare, in una riga: «21-15-9 Thrusters · Pull-up»,
 * «5 Power Clean · 3 Front Squat» per un EMOM, «team of 2: 100 cal Row · …»
 * per un testo libero. Vuota se il nome dell'esercizio basta già.
 */
export function describePrescriptionContent(exercise) {
	if (!exercise || typeof exercise !== "object") return "";

	if (exercise.exercise_type === "conditioning") {
		const movements = movementList(
			Array.isArray(exercise.conditioning_exercises)
				? exercise.conditioning_exercises
				: [],
		);
		if (movements.length > 0) return movements.join(" · ");
		// Conditioning EMOM: i movimenti stanno nei round, non in
		// `conditioning_exercises`.
		if (String(exercise.conditioning_type || "").toLowerCase() === "emom") {
			const emomMovements = movementList(emomRoundItems(exercise));
			if (emomMovements.length > 0) return emomMovements.join(" · ");
		}
		return freeTextLine(
			exercise.conditioning_free_text || exercise.custom_workout_text,
		);
	}

	// Strength EMOM: round con gli elementi, o la lista storica `emom_movements`.
	if (String(exercise.methodology || "").toLowerCase() === "emom") {
		const fromRounds = movementList(emomRoundItems(exercise));
		const movements =
			fromRounds.length > 0
				? fromRounds
				: movementList(
						Array.isArray(exercise.emom_movements)
							? exercise.emom_movements
							: [],
					);
		return movements.join(" · ");
	}

	const superset = Array.isArray(exercise.superset_exercises)
		? exercise.superset_exercises
		: [];
	if (superset.length > 0) {
		return superset
			.map((item) => String(item?.exercise_name || "").trim())
			.filter(Boolean)
			.join(" + ");
	}

	return "";
}

/**
 * Cosa è stato fatto, in una riga: «3 × 100 kg × 5 · RPE 8», «12:34»,
 * «5 round + 3 rep», «HF 6.21 · 4.82s». Stringa vuota se non c'è nulla da dire.
 */
export function summarizeResult(result, exercise, language = "it") {
	if (!result) return "";
	const parts = [];

	if (result.shooting && typeof result.shooting === "object") {
		const shooting = summarizeShooting(result.shooting);
		if (shooting) parts.push(shooting);
	} else {
		const condition = resolveConditioningResultPresentation({
			result,
			prescriptionExercise: exercise,
			programExercise: exercise,
			language,
		});
		const rows = Array.isArray(result.result_set_rows)
			? result.result_set_rows
			: [];
		const superset = Array.isArray(result.superset_details)
			? result.superset_details
			: [];
		const schema = summarizeSchema(result, exercise);

		if (rows.length > 0 && summarizeSetRows(rows)) {
			parts.push(summarizeSetRows(rows));
		} else if (superset.length > 0) {
			parts.push(
				superset
					.map((detail) =>
						[
							detail?.exercise_name,
							isMeaningful(detail?.weight_kg) ? `${detail.weight_kg} kg` : "",
							repsOf(detail) ? `× ${repsOf(detail)}` : "",
						]
							.filter(Boolean)
							.join(" "),
					)
					.filter(Boolean)
					.join(" · "),
			);
		} else if (schema) {
			parts.push(schema);
		} else if (condition.isConditioning) {
			const clock = formatClock(result.time_seconds);
			if (clock) parts.push(clock);
			const score = condition.amrapScoreLabel || condition.displayScore;
			if (hasContent(score) && !(clock && condition.condType === "for_time"))
				parts.push(String(score));
		} else {
			const weight = isMeaningful(result.weight_kg)
				? `${result.weight_kg} kg`
				: "";
			const reps = repsOf(result);
			if (weight && reps) parts.push(`${weight} × ${reps}`);
			else if (weight || reps) parts.push(weight || reps);
			const clock = formatClock(result.time_seconds);
			if (clock) parts.push(clock);
			if (hasContent(result.score)) parts.push(String(result.score));
		}
		if ((result.interval_scores || []).length > 0 && parts.length === 0)
			parts.push(result.interval_scores.join(" · "));
		if (condition.isConditioning && hasContent(result.scaling))
			parts.push(String(result.scaling));
	}

	if (isMeaningful(result.rpe)) parts.push(`RPE ${result.rpe}`);
	return parts.filter(Boolean).join(" · ");
}
